import { Button, Descriptions, Form, Layout, Popconfirm, Row, Select, Space } from 'antd';
import { useState, useEffect } from 'react';
import { DATE_TIME_FORMAT, FormatDate } from '../../../constants';

export interface SharedCaseAffirmationsAudit {
  completedAt?: string;
  completedBy?: {
    displayName: string;
  };
  result?: string;
}

interface AffirmationsChecklistProps<T> {
  affirmations: T;
  audit: SharedCaseAffirmationsAudit;
  fieldName: string;
  onSave: (values: any) => Promise<void>;
  fieldNamesMap: Map<string, string>;
  state: string;
  disabled: boolean;
  isSuccess: boolean;
}

export const AffirmationsChecklist = <T extends Record<string, any>>({
  affirmations,
  audit,
  fieldName,
  onSave,
  fieldNamesMap,
  state,
  disabled,
  isSuccess
}: AffirmationsChecklistProps<T>) => {
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(!!audit?.completedBy);
  const [isCompleteVisible, setIsCompleteVisible] = useState(false);
  const [hasUnchangedChanges, setHasUnchangedChanges] = useState(false);


  const initialValues = {
    ...affirmations
  };

  const [affirmationValues, setAffirmationValues] = useState(initialValues);

  const {
    isIdDocumentAcceptable,
    isIdDocumentDetailsAcceptable,
    isPhotoAcceptable,
    isNotarizedIdAcceptable,
    isNotaryAcceptable,
    isNameAcceptable,
    isTranslationAcceptable,
    isCredentialAcceptable,
    isCredentialDetailsAcceptable,
    isDateOfBirthAcceptable,
    isEntityAcceptable,
    isCredentialSentForVerification,
    isReturnedFromCorrectEntity,
    isVerificationMethodAcceptable,
    isVerificationComplete,
    isVerificationInEnglishOrWithTranslation,
    isEntityDetailCorrect,
    isCredentialReturnedFromEntity,
    isVerificationStatusComplete,
    isVerificationComponentPacketCompiled,
    isDestinationAcceptable,
    isReportCompiled,
    isReportSent
  } = affirmations ?? {};

  const areIdVerificationAffirmationsComplete = !!isIdDocumentAcceptable && !!isIdDocumentDetailsAcceptable && !!isPhotoAcceptable;

  const areIdVerificationNotaryAffirmationsComplete = !!isNotarizedIdAcceptable && !!isNotaryAcceptable;

  const areCredentialAffirmationsComplete =
    !!isNameAcceptable &&
    !!isTranslationAcceptable &&
    !!isCredentialAcceptable &&
    !!isCredentialDetailsAcceptable &&
    !!isDateOfBirthAcceptable &&
    !!isEntityAcceptable &&
    !!isCredentialSentForVerification;

  const areCredentialVerificationAffirmationsComplete =
    !!isReturnedFromCorrectEntity &&
    !!isVerificationMethodAcceptable &&
    !!isVerificationComplete &&
    !!isVerificationInEnglishOrWithTranslation &&
    !!isEntityDetailCorrect &&
    !!isCredentialReturnedFromEntity &&
    !!isVerificationStatusComplete &&
    !!isVerificationComponentPacketCompiled;

  const areSendReportAffirmationsComplete = !!isDestinationAcceptable && !!isReportCompiled && !!isReportSent;

  useEffect(() => {
    if (audit?.completedBy) {
      setIsComplete(true);
    }

    if (
      affirmations &&
      (areCredentialAffirmationsComplete ||
        areIdVerificationAffirmationsComplete ||
        areIdVerificationNotaryAffirmationsComplete ||
        areCredentialVerificationAffirmationsComplete ||
        areSendReportAffirmationsComplete) &&
      (!audit || !audit?.completedBy)
    ) {
      setIsCompleteVisible(true);
    }
  }, [audit, affirmations]);

  const handleMenuClick = (key: keyof T) => (e: any) => {
    setAffirmationValues((prev) => ({
      ...prev,
      [key]: e.key
    }));
  };

  const mapNullToUndefined = (obj: any) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (obj[key] === null) {
        return { ...acc, [key]: undefined };
      }
      return { ...acc, [key]: obj[key] };
    }, {});
  };

  const areAllValuesFilled = (values: Record<string, any>) => {
    return Object.values(values).every((value) => value !== undefined && value !== null && value !== '');
  };

  const handleSave = async () => {
    try {
      setFormLoading(true);
      const input = {
        [fieldName]: {
          ...mapNullToUndefined(affirmationValues)
        }
      };
      await onSave(input);
      console.log('onSuccess', isSuccess);
      setFormLoading(false);
      if (areAllValuesFilled(affirmationValues)) {
        setIsCompleteVisible(true);
      } else {
        setIsCompleteVisible(false);
      }
    } catch (error) {
      console.error('Error saving the review:', error);
      setFormLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setFormLoading(true);
      const input = {
        [fieldName]: {
          audit: {
            setAffirmationsComplete: true
          }
        }
      };
      await onSave(input);
      setFormLoading(false);
      setIsComplete(true);
      setIsCompleteVisible(false);
    } catch (error) {
      console.error('Error completing the affirmation:', error);
      setFormLoading(false);
    }
  };

  const handleReopenAffirmation = async () => {
    try {
      setFormLoading(true);
      const input = {
        [fieldName]: {
          audit: {
            setAffirmationsComplete: false
          }
        }
      };
      await onSave(input);
      setFormLoading(false);
      setIsComplete(false);
      setIsCompleteVisible(false);
    } catch (error) {
      console.error('Error reopening the affirmation:', error);
      setFormLoading(false);
    }
  };

  const handleCancel = () => {
    setAffirmationValues(initialValues);
    setIsCompleteVisible(false);
  };

  const renderAffirmations = () => {
    return (
      <Form form={form} style={{ width: '100%' }} layout="vertical" initialValues={initialValues} onFinish={handleSave}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {Object.keys(affirmations).map((field) => {
            const fieldName = fieldNamesMap.get(field) || field;
            return (
              <Form.Item
                key={field}
                name={['affirmations', field]}
                rules={[
                  {
                    required: true,
                    message: `Please select a value for ${fieldName}`
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <div key={field} style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ display: 'block' }}>
                    <Select
                      value={affirmationValues[field]}
                      onChange={(value) => {
                        handleMenuClick(field as keyof typeof affirmationValues)({ key: value });
                        setHasUnchangedChanges(true);
                      }}
                      disabled={isComplete || disabled}
                      style={{ width: 70 }}
                    >
                      <Select.Option key="Yes" value="Yes">
                        Yes
                      </Select.Option>

                      <Select.Option key="No" value="No">
                        No
                      </Select.Option>

                      <Select.Option key="N/A" value="N/A">
                        N/A
                      </Select.Option>
                    </Select>
                  </span>
                  <span>{fieldName + '?'}</span>
                </div>
              </Form.Item>
            );
          })}
        </div>
      </Form>
    );
  };

  return (
    <Layout style={{ borderRadius: '10px' }}>
      <div>
        {renderAffirmations()}
        <br />
        {!isComplete && (
          <Space size="large" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: '75px', height: '32px' }} onClick={handleCancel} disabled={isComplete || disabled || formLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              value={'save'}
              style={{
                background: isComplete || disabled ? '#9ea0b4' : '#3F4373',
                color: '#D9D9D9',
                padding: '4px, 15px, 4px, 15px',
                gap: '8px'
              }}
              onClick={() => {
                setHasUnchangedChanges(false);
                handleSave();
              }}
              disabled={isComplete || disabled}
              loading={formLoading}
            >
              Save
            </Button>
          </Space>
        )}
        <br />
        <br />
        {isCompleteVisible && (
          <>
            {hasUnchangedChanges ? (
              <Popconfirm title="You have unsaved changes. Are you sure you want to complete?" onConfirm={handleComplete} okText="Yes" cancelText="No">
                <Button
                  type="primary"
                  style={{
                    background: '#097D5A',
                    color: '#D9D9D9',
                    width: '100%',
                    marginBottom: '20px'
                  }}
                  disabled={disabled}
                >
                  Complete
                </Button>
              </Popconfirm>
            ) : (
              <Button
                type="primary"
                style={{
                  background: '#097D5A',
                  color: '#D9D9D9',
                  width: '100%',
                  marginBottom: '20px'
                }}
                onClick={handleComplete}
                disabled={disabled}
              >
                Complete
              </Button>
            )}
          </>
        )}
        {isComplete && !isCompleteVisible && (
          <>
            <Row gutter={[5, 5]}>
              <Descriptions title={undefined} layout="vertical" size="small" column={2} className="w-[600px]">
                <Descriptions.Item label="Completed By">{audit?.completedBy?.displayName?.toString()}</Descriptions.Item>
                <Descriptions.Item label="Result">
                  {/* Capitalize the first letter of the result */}
                  {audit?.result ? audit.result.charAt(0).toUpperCase() + audit.result.slice(1) : ''}
                </Descriptions.Item>
                <Descriptions.Item span={2}>&nbsp;</Descriptions.Item>
                <Descriptions.Item label="Completed At">{FormatDate(audit?.completedAt as string, DATE_TIME_FORMAT)}</Descriptions.Item>
              </Descriptions>
            </Row>
            {state !== 'decisionRendered' && (
              <Button
                type="primary"
                style={{
                  background: '#FFFFB8',
                  color: '#000000',
                  width: '100%',
                  marginTop: '10px'
                }}
                onClick={handleReopenAffirmation}
                loading={formLoading}
                disabled={disabled}
              >
                Reopen Affirmation
              </Button>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};
